import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { http } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { LineChart as LineIcon, Trophy, MapPin } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const shortMonth = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
const longMonth = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

function useAdminData(url) {
  const [data, setData] = useState(null);
  const { lastEvent } = useRealtime();
  const load = () => http.get(url).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (lastEvent?.event === "facture_changed") load(); }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}

export function RevenueTrendCard({ month }) {
  const rows = useAdminData(`/stats/revenue-trend?end=${month}`);
  if (!rows) return null;
  const data = rows.map((r) => ({ ...r, label: shortMonth(r.month) }));
  const sumInv = rows.reduce((a, r) => a + r.invoiced_ttc, 0);
  const sumPaid = rows.reduce((a, r) => a + r.paid_ttc, 0);
  return (
    <div className="border border-slate-200 bg-white rounded-md p-6 mb-8" data-testid="revenue-trend-card">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><LineIcon className="h-5 w-5 text-sky-600" /> Tendance sur 12 mois</h3>
          <p className="text-xs text-slate-500">Facturé et encaissé TTC, de {longMonth(rows[0].month)} à {longMonth(rows[rows.length - 1].month)}</p>
        </div>
        <div className="flex gap-6 text-right">
          <div><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Facturé 12 mois</div><div className="font-display text-xl font-extrabold text-sky-700" data-testid="trend-sum-invoiced">{eur(sumInv)}</div></div>
          <div><div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Encaissé 12 mois</div><div className="font-display text-xl font-extrabold text-emerald-700" data-testid="trend-sum-paid">{eur(sumPaid)}</div></div>
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#cbd5e1" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} tickFormatter={(v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(v)} width={56} />
            <Tooltip formatter={(v, n) => [eur(v), n]} labelStyle={{ fontWeight: 700 }} contentStyle={{ borderRadius: 6, borderColor: "#e2e8f0", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="invoiced_ttc" name="Facturé TTC" stroke="#0284c7" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="paid_ttc" name="Encaissé TTC" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function RevenueByClientCard({ month }) {
  const d = useAdminData(`/stats/revenue-by-client?month=${month}`);
  if (!d) return null;
  const max = Math.max(1, ...d.clients.map((c) => c.invoiced_ht));
  return (
    <div className="border border-slate-200 bg-white rounded-md p-6 mb-8" data-testid="revenue-by-client-card">
      <h3 className="font-display text-xl font-bold flex items-center gap-2 mb-1"><Trophy className="h-5 w-5 text-amber-500" /> Classement clients — <span className="capitalize">{longMonth(d.month)}</span></h3>
      <p className="text-xs text-slate-500 mb-4">Chiffre d'affaires HT facturé dans le mois, du plus important au plus faible</p>
      {d.clients.length === 0 && <div className="text-sm text-slate-500 text-center py-6">Aucune facture émise ce mois-ci.</div>}
      <ol className="space-y-2.5">
        {d.clients.map((c, i) => (
          <li key={c.client_id || c.client_name} className="grid grid-cols-[28px_1fr_auto] items-center gap-3" data-testid={`client-rank-${i + 1}`}>
            <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${i === 0 ? "bg-amber-400 text-white" : i === 1 ? "bg-slate-300 text-slate-800" : i === 2 ? "bg-orange-300 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
            <div className="min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                {c.client_id ? <Link to="/clients" className="font-semibold text-sm truncate hover:underline">{c.client_name}</Link> : <span className="font-semibold text-sm truncate">{c.client_name}</span>}
                <span className="text-[11px] text-slate-500 shrink-0">{c.count} fact. · {c.share_pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden">
                <div className="h-full rounded-full bg-sky-500" style={{ width: `${(c.invoiced_ht / max) * 100}%` }} />
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono font-bold text-sm">{eur(c.invoiced_ht)} HT</div>
              <div className="text-[10px] text-emerald-700 font-mono">{eur(c.paid_ttc)} encaissé TTC</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function RevenueBySiteCard({ month }) {
  const d = useAdminData(`/stats/revenue-by-site?month=${month}`);
  if (!d) return null;
  const max = Math.max(1, ...d.sites.map((x) => x.invoiced_ht));
  return (
    <div className="border border-slate-200 bg-white rounded-md p-6 mb-8" data-testid="revenue-by-site-card">
      <h3 className="font-display text-xl font-bold flex items-center gap-2 mb-1"><MapPin className="h-5 w-5 text-indigo-500" /> Classement sites — <span className="capitalize">{longMonth(d.month)}</span></h3>
      <p className="text-xs text-slate-500 mb-4">CA HT facturé par site (les factures multi-sites sont réparties selon leurs lignes)</p>
      {d.sites.length === 0 && <div className="text-sm text-slate-500 text-center py-6">Aucune facture émise ce mois-ci.</div>}
      <ol className="space-y-2.5">
        {d.sites.map((x, i) => (
          <li key={x.site_id || x.site_name} className="grid grid-cols-[28px_1fr_auto] items-center gap-3" data-testid={`site-rank-${i + 1}`}>
            <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${i === 0 ? "bg-amber-400 text-white" : i === 1 ? "bg-slate-300 text-slate-800" : i === 2 ? "bg-orange-300 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
            <div className="min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className={`font-semibold text-sm truncate ${x.site_id ? "" : "italic text-slate-500"}`}>{x.site_name}</span>
                <span className="text-[11px] text-slate-500 shrink-0">{x.count} fact. · {x.share_pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${(x.invoiced_ht / max) * 100}%` }} /></div>
            </div>
            <div className="font-mono font-bold text-sm text-right">{eur(x.invoiced_ht)} HT</div>
          </li>
        ))}
      </ol>
    </div>
  );
}
